import React, { useState } from "react";
import { useMutation } from "react-query";
import { Link } from "react-router-dom";

import styled, { css } from "styled-components";

import Dropdown from "components/atoms/Dropdown";
import Icon from "components/atoms/Icon";
import Image from "components/atoms/Image";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Button, { BUTTON_STYLES } from "components/atoms/button";

import AdminChangeUserModal from "components/page/modal/AdminChangeUserModal";
import PageHeaderMenu from "components/templates/header/PageHeaderMenu";
import PageHeaderNavItem from "components/templates/header/PageHeaderNavItem";

import { useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { useDevice } from "utils/device";
import { logout } from "utils/request/auth";

const Container = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 8px;
    width: 100%;
    height: ${Constants.MOBILE_HEADER_HEIGHT}px;
    background-color: ${(props) => (props.isIndexPage ? "rgba(255, 255, 255, 0.8)" : "var(--color-White)")};
    border-bottom: ${(props) => (props.isIndexPage ? "transparent" : "solid 1px var(--color-Line)")};
    z-index: 100;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            padding: 0 16px;
            min-width: 768px;
            height: ${Constants.TABLET_OR_PC_HEADER_HEIGHT}px;
        `};

    ${(props) =>
        props.device.isPC &&
        css`
            padding: 0 16px;
        `};
`;

const LogoWrap = styled.div`
    transform: scale(0.8);

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            transform: scale(1);
        `};
`;

const HamburgerButtonWrap = styled.div`
    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            display: none;
        `};
`;

const NavSection = styled.div`
    display: none;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            display: flex;
            align-items: center;
            height: 36px;

            a {
                height: 100%;
            }
        `};
`;

const UserIdWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 8px 0 20px;
    height: 32px;
    border-radius: 6px;
    transition: all 80ms ease-in;
    cursor: pointer;
`;

const MobileBlankBox = styled.div`
    width: 24px;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            display: none;
        `};
`;

const GNBBackground = styled.div`
    position: fixed;
    top: ${Constants.MOBILE_HEADER_HEIGHT}px;
    left: 0;
    width: 100vw;
    height: calc(100vh - ${Constants.MOBILE_HEADER_HEIGHT}px);
    background-color: rgba(0, 0, 0, 0.4);
    visibility: ${({ menuVisible }) => (menuVisible ? "visible" : "hidden")};
    opacity: ${({ menuVisible }) => (menuVisible ? "1" : "0")};
    transition: all 120ms ease-in;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            display: none;
        `};
`;

const GNBSideMenuContainer = styled.div`
    width: 100vw;
    height: 100%;
    background-color: var(--color-White);
    transition: all 120ms ease-in-out;
`;

const MenuItem = styled.div`
    width: 100%;
    height: 56px;
    background-color: var(--color-White);
`;

const MobileUserMenuWrap = styled.div`
    width: 100%;
`;

const MobileUserIDWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 32px;
    width: 100%;
    border-top: solid 2px var(--color-Line);
`;

const ProfileInitialButton = styled.div`
    width: 32px;
    height: 32px;
    border: ${({ menuOpen }) => (menuOpen ? "2px solid var(--color-Button1)" : "1px solid var(--color-Line)")};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 1.4rem;
    font-weight: 600;
    text-transform: uppercase;
    transition: all 120ms ease-in-out;

    background-color: var(--color-ButtonHover4);
    color: var(--color-Black);

    user-select: none;
    pointer-events: none;
`;

function PageHeader({ ...props }) {
    const device = useDevice();

    const [menuOpen, setMenuOpen] = useState(false);
    const [menuVisible, setMenuVisible] = useState(false);
    const [showAdminChangeUserModal, setShowAdminChangeUserModal] = useState(false);

    const userQuery = useUser();
    const user = userQuery.data;

    const logoutMutation = useMutation(logout, {
        onSuccess: (data) => {
            localStorage.removeItem("token");
            sessionStorage.clear();
            window.location = "/";
        },
        onError: (e) => {
            Alert("warn", "에러", "서버에 오류가 발생하였습니다.");
        },
    });

    const toggleMenuVisibility = () => {
        setMenuVisible(!menuVisible);
    };

    const handleCustomerSupportClick = () => {
        window.open("https://support.preppaper.com/", "_blank");
    };

    return (
        <Container device={device} isIndexPage={props.isIndexPage}>
            <HamburgerButtonWrap device={device}>
                <Button onClick={toggleMenuVisibility} {...BUTTON_STYLES.iconGhost}>
                    <Icon name={"hamburger"} />
                </Button>
            </HamburgerButtonWrap>
            <LogoWrap device={device}>
                <Link as={Link} to={"/"} reloadDocument>
                    <Image name={"headerLogo"} />
                </Link>
            </LogoWrap>
            <NavSection device={device}>
                <>
                    <PageHeaderNavItem linkAddress={"/plan"} menuName={"요금안내"} />
                    <PageHeaderNavItem menuName={"고객지원"} onClick={handleCustomerSupportClick} />
                    {user?.is_authenticated ? (
                        <>
                            <Dropdown
                                open={menuOpen}
                                onOpenChange={setMenuOpen}
                                placement="bottom-end"
                                offset={4}
                                zIndex={101}
                                anchor={
                                    <UserIdWrap onClick={() => setMenuOpen(!menuOpen)} menuOpen={menuOpen}>
                                        <Tooltip message={user.email} placement="bottom">
                                            <ProfileInitialButton menuOpen={menuOpen}>
                                                {user.email.trim()?.[0] || "?"}
                                            </ProfileInitialButton>
                                        </Tooltip>
                                    </UserIdWrap>
                                }
                            >
                                <PageHeaderMenu
                                    isStaff={user.is_staff || false}
                                    logoutClick={(e) => logoutMutation.mutate()}
                                    setShowAdminChangeUserModal={setShowAdminChangeUserModal}
                                />
                            </Dropdown>
                        </>
                    ) : (
                        <PageHeaderNavItem linkAddress={"/login"} menuName={"로그인"} />
                    )}
                </>
            </NavSection>
            <MobileBlankBox device={device} />
            <GNBBackground device={device} menuVisible={menuVisible}>
                <GNBSideMenuContainer>
                    <Link as={Link} to={"/plan"}>
                        <Button {...BUTTON_STYLES.menuItemText} buttonText={"요금안내"} />
                    </Link>
                    <MenuItem onClick={handleCustomerSupportClick}>
                        <Button {...BUTTON_STYLES.menuItemText} buttonText={"고객지원"} />
                    </MenuItem>
                    {user?.is_authenticated ? (
                        <>
                            <MobileUserMenuWrap>
                                <Link as={Link} to={"/account"}>
                                    <Button {...BUTTON_STYLES.menuItemText} buttonText={"계정정보"} />
                                </Link>
                                <Link as={Link} to={"/settings"}>
                                    <Button {...BUTTON_STYLES.menuItemText} buttonText={"환경설정"} />
                                </Link>
                                <Button
                                    {...BUTTON_STYLES.menuItemText}
                                    buttonText={"로그아웃"}
                                    onClick={(e) => logoutMutation.mutate()}
                                />
                            </MobileUserMenuWrap>
                            <MobileUserIDWrap>
                                <Button {...BUTTON_STYLES.menuItemText} buttonText={user.email} />
                            </MobileUserIDWrap>
                        </>
                    ) : (
                        <Link as={Link} to={"/login"}>
                            <Button {...BUTTON_STYLES.menuItemText} buttonText={"로그인"} />
                        </Link>
                    )}
                </GNBSideMenuContainer>
            </GNBBackground>
            {showAdminChangeUserModal && <AdminChangeUserModal exitModal={() => setShowAdminChangeUserModal(false)} />}
        </Container>
    );
}

export default PageHeader;
